import { CRUDAction, FeatureKey } from 'common/constants/permission'
import { fNumber } from 'common/utils/formatNumber'
import { usePermitSomeActionToFeature } from 'modules/user/api/selector'
import { useState, useEffect, memo, useCallback, useMemo, useRef } from 'react'
import palette from 'theme/palette'

import { LABELS } from '../constants'
import { MOBILE_FONT_SIZE } from '../styles'

interface EditableCellProps {
  value: number | string
  onChange: (value: string) => Promise<void>
  editable: boolean
  label: string
  group?: string
  subGroup?: string
  id?: number
  columnIndex?: number
  isMobile?: boolean
  // NK-273: colour when the value is over this limit; null = no band covers
  // the shrimp age (never coloured); undefined = the fixed rule below.
  colorThreshold?: number | null
}

const getCellStyle = (
  inputValue: string,
  editable: boolean,
  label: string,
  isMobile?: boolean,
  colorThreshold?: number | null
) => {
  const labelLower = label.toLowerCase()
  let bgColor = palette.light.background.default
  const numValue = Number(inputValue)
  const isValidNumber = !isNaN(numValue)

  if (labelLower.includes(LABELS.AGE.toLowerCase())) {
    bgColor = palette.light.grey[200]
  } else {
    let shouldShowColor = false
    if (isValidNumber && inputValue.trim() !== '' && colorThreshold !== null) {
      if (colorThreshold !== undefined) shouldShowColor = numValue > colorThreshold
      // Treatment ponds (no shrimp age), or settings not loaded yet.
      else if (labelLower === LABELS.YELLOW.toLowerCase()) shouldShowColor = numValue > 200
      else shouldShowColor = numValue > 0
    }

    if (shouldShowColor) {
      if (labelLower === LABELS.YELLOW.toLowerCase()) bgColor = palette.light.warning.main
      else if (labelLower === LABELS.GREEN.toLowerCase()) bgColor = palette.light.primary.main
      else if (labelLower === LABELS.BLACK.toLowerCase()) bgColor = palette.light.grey[500]
      else if (labelLower === LABELS.GREEN_YELLOW.toLowerCase())
        bgColor = palette.light.success.light
    } else if (!editable) {
      bgColor = palette.light.grey[200]
    }
  }

  return {
    width: '100%',
    height: '40px',
    border: `1.5px solid ${palette.light.grey[300]}`,
    outline: 'none',
    padding: '0 8px',
    lineHeight: '40px',
    fontSize: isMobile ? MOBILE_FONT_SIZE : '0.875rem',
    fontWeight: isMobile ? 700 : undefined,
    backgroundColor: bgColor,
    textAlign: 'center' as const,
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
  }
}

export const EditableCell = memo(
  ({
    value,
    onChange,
    editable,
    label,
    group,
    subGroup,
    id,
    columnIndex,
    isMobile,
    colorThreshold,
  }: EditableCellProps) => {
    const [inputValue, setInputValue] = useState(value ?? '') // ใช้ค่าเริ่มต้น
    const isMountedRef = useRef(true)
    const allowEdit = usePermitSomeActionToFeature(
      [CRUDAction.Create, CRUDAction.Update, CRUDAction.Delete],
      FeatureKey.Lab
    )

    useEffect(() => {
      setInputValue(value)
    }, [value])

    // NK-305: latest typed value for the unmount flush below.
    const pendingRef = useRef({ inputValue, value, onChange })
    pendingRef.current = { inputValue, value, onChange }
    // True between a keystroke and the save it leads to, so the unmount flush
    // only ever sends what the user typed (rows unmount while scrolling too).
    const isDirtyRef = useRef(false)

    useEffect(() => {
      return () => {
        isMountedRef.current = false
        // NK-305: a value typed but never blurred (tab switch, row virtualised
        // away, page closing) used to be dropped. Send it as blur would have.
        if (!isDirtyRef.current) return
        const pending = pendingRef.current
        const newValue = String(pending.inputValue)
        if (newValue === '' && pending.value === '-') return
        if (newValue !== String(pending.value)) {
          pending.onChange(newValue).catch(() => undefined)
        }
      }
    }, [])

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      // The input shows the value with thousand separators ("9,999"), so an
      // edit arrives with commas in it ("9,99" after a backspace). Strip them
      // before validating, or every edit past 999 is rejected.
      let newValue = e.target.value.replace(/,/g, '')

      if (newValue !== '' && !/^\d+$/.test(newValue)) {
        return
      }

      if (newValue.length > 1 && newValue.startsWith('0')) {
        newValue = newValue.replace(/^0+/, '') || '0'
      }

      isDirtyRef.current = true
      setInputValue(newValue === '' ? '-' : newValue)
    }, [])

    const handleBlur = useCallback(() => {
      isDirtyRef.current = false
      const newValue = String(inputValue)

      if (newValue === '' && value === '-') return

      if (newValue !== String(value)) {
        onChange(newValue).catch(() => {
          if (isMountedRef.current) {
            setInputValue(String(value))
          }
        })
      }
    }, [inputValue, value, onChange])

    const cellStyle = useMemo(
      () => getCellStyle(String(inputValue), editable, label, isMobile, colorThreshold),
      [inputValue, editable, label, isMobile, colorThreshold]
    )

    if (!editable) {
      return <div style={cellStyle}>{value}</div>
    }

    return (
      <input
        type="text"
        value={inputValue === '-' ? '' : fNumber(inputValue, { disabledDecimal: true })}
        onChange={handleChange}
        onBlur={handleBlur}
        style={cellStyle}
        data-group={group}
        data-subgroup={subGroup}
        data-label={label}
        data-id={id}
        data-column-index={columnIndex}
        disabled={!allowEdit}
        placeholder="-"
      />
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.value === nextProps.value &&
      prevProps.editable === nextProps.editable &&
      prevProps.label === nextProps.label &&
      prevProps.group === nextProps.group &&
      prevProps.subGroup === nextProps.subGroup &&
      prevProps.id === nextProps.id &&
      prevProps.colorThreshold === nextProps.colorThreshold
    )
  }
)

EditableCell.displayName = 'EditableCell'
